// Rates normalised by group size (dataset analytics): a subgroup's figure is only meaningful beside the base rate of
// everyone ("39% of EPP members vs 26% of all"; "3.2 appearances per EPP member vs 1.9 for all"). Pure arithmetic and
// wording, computed in code and never by a model; the aggregate (`packages/db/src/claims/aggregate.ts`) feeds it its
// tallies and the chart table and panel print what it returns.

import { CHART_WORDS, type ChartWords } from './chart-words';
import type { AggregateRate, AggregateRateInfo, AggregateResult } from './charts';

/** Percentage of `part` in `whole` (0 when the group is empty, never NaN). */
export function shareOf(part: number, whole: number): number {
  return whole > 0 ? (part / whole) * 100 : 0;
}

/** Links or amounts per entity of a group of `size` entities (0 for an empty group). */
export function perEntity(units: number, size: number): number {
  return size > 0 ? units / size : 0;
}

/** Rounds for display: a share to one decimal below 10 % and whole numbers above, a per-entity rate to two decimals. */
export function roundRate(v: number, kind: AggregateRate): number {
  if (kind === 'share') return Math.abs(v) < 10 ? Math.round(v * 10) / 10 : Math.round(v);
  return Math.round(v * 100) / 100;
}

/** "39%", "4.5%", "3.25". */
export function formatRate(v: number, kind: AggregateRate): string {
  const n = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(roundRate(v, kind));
  return kind === 'share' ? `${n}%` : n;
}

/** Percentage points (share) or the plain difference (per entity) between a group's rate and the base rate. */
export function rateGap(value: number, base: number, kind: AggregateRate): string {
  const d = roundRate(value, kind) - roundRate(base, kind);
  const n = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2, signDisplay: 'exceptZero' }).format(Math.round(d * 100) / 100);
  return kind === 'share' ? `${n} pt` : n;
}

/**
 * The comparison printed beside a subgroup's figure: "39% of EPP vs 26% of all" (share, for one split value) or
 * "3.25 per entity in EPP vs 1.9 for all" (per entity).
 */
export function rateComparison(kind: AggregateRate, value: number, base: number, group: string): string {
  if (kind === 'share') return `${formatRate(value, kind)} of ${group} vs ${formatRate(base, kind)} of all`;
  return `${formatRate(value, kind)} per entity in ${group} vs ${formatRate(base, kind)} for all`;
}

/** One line per group (and split value) comparing its rate with the base rate; "Other" and empty groups included. */
export function rateLines(data: Pick<AggregateResult, 'buckets' | 'splits' | 'rate'>): string[] {
  const r = data.rate;
  if (!r) return [];
  return data.buckets.flatMap((b, i) => {
    const n = r.sizes[i] ?? 0;
    const size = `(n = ${n.toLocaleString('en-GB')})`;
    if (r.kind === 'per_entity') return [`${b.label}: ${rateComparison('per_entity', b.value, r.base[0] ?? 0, b.label)} ${size}`];
    return data.splits.map((s, k) => `${s.label}: ${rateComparison('share', b.split?.[k] ?? 0, r.base[k] ?? 0, b.label)} ${size}`);
  });
}

/** The base rates: per split for a share, one number per entity; from raw tallies over all matched entities. */
export function baseRates(kind: AggregateRate, totals: number[], population: number): number[] {
  return totals.map(t => (kind === 'share' ? shareOf(t, population) : perEntity(t, population)));
}

/** The table columns a rate chart adds: the base rate beside every subgroup figure, and the group size. */
export function rateTable(
  data: Pick<AggregateResult, 'buckets' | 'splits' | 'groupLabel' | 'measureLabel'> & { rate: AggregateRateInfo },
  words: ChartWords = CHART_WORDS.en,
): { headers: string[]; rows: string[][] } {
  const r = data.rate;
  const n = (v: number) => v.toLocaleString('en-GB');
  if (r.kind === 'per_entity') {
    const headers = [data.groupLabel, data.measureLabel, words.allMatched, words.difference, words.inGroup];
    const base = r.base[0] ?? 0;
    const rows = data.buckets.map((b, i) => [
      b.label,
      formatRate(b.value, 'per_entity'),
      formatRate(base, 'per_entity'),
      rateGap(b.value, base, 'per_entity'),
      n(r.sizes[i] ?? 0),
    ]);
    rows.push([words.allMatched, formatRate(base, 'per_entity'), formatRate(base, 'per_entity'), '0', n(r.population)]);
    return { headers, rows };
  }
  const headers = [
    data.groupLabel,
    ...data.splits.map((s, k) => `${s.label} (${words.all}: ${formatRate(r.base[k] ?? 0, 'share')})`),
    words.inGroup,
  ];
  const rows = data.buckets.map((b, i) => [
    b.label,
    ...data.splits.map((_, k) => {
      const v = b.split?.[k] ?? 0;
      return `${formatRate(v, 'share')} (${rateGap(v, r.base[k] ?? 0, 'share')})`;
    }),
    n(r.sizes[i] ?? 0),
  ]);
  rows.push([words.allMatched, ...data.splits.map((_, k) => formatRate(r.base[k] ?? 0, 'share')), n(r.population)]);
  return { headers, rows };
}

export interface MethodInput {
  /** What is counted: "entities", "links (participation)", "amounts in EUR". */
  measure: 'count' | 'links' | 'sum';
  currency?: string | null;
  group: string;
  split?: string | null;
  rate?: AggregateRate | null;
  entities: number;
  /** A link to an event without a date of its own took the event's date. */
  eventDates?: boolean;
  /** Links are participation links (an appearance register). */
  roles?: boolean;
}

/** How the numbers were computed, in one or two plain sentences (always shown beside the chart). */
export function aggregateMethod(m: MethodInput): string {
  const n = m.entities.toLocaleString('en-GB');
  const what =
    m.measure === 'sum'
      ? `the amounts of their money links in ${m.currency ?? 'one currency'} (ranges as their middle; no conversion)`
      : m.measure === 'links'
        ? `their ${m.roles ? 'participation links (one per appearance)' : 'links'}, each counted once per group`
        : 'the entities, each counted once per group';
  const by = m.split ? `${m.group}, split by ${m.split}` : m.group;
  const parts = [`Computed from the claims of ${n} matched ${m.entities === 1 ? 'entity' : 'entities'}: ${what}, by ${by}.`];
  if (m.rate === 'share')
    parts.push(
      `Each figure is the share of the group's entities with that ${m.split ?? 'value'}, beside the same share among all ${n} (the base rate); an entity can count in more than one part.`,
    );
  if (m.rate === 'per_entity')
    parts.push(
      `Each figure is divided by the number of entities in the group (entities with none count as 0), beside the same rate for all ${n} (the base rate).`,
    );
  if (m.eventDates) parts.push('A link with no date of its own takes the date of its event.');
  parts.push('Rejected and off-record claims are not counted.');
  return parts.join(' ');
}
